import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useAuth } from '@/modules/auth/presentation/context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useAccessRolesQuery } from '../../application/profileQueries';
import type { AccessRole } from '../../domain/entities/AccessRole';

export function ProfilePickerPage() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const auth = useAuth();
  const profile = useProfile();
  const { data: roles = [], isLoading, isError } = useAccessRolesQuery();

  const [sel, setSel] = useState<number | null>(
    () => profile.selectedRole?.idAccessRole ?? null,
  );
  const [search, setSearch] = useState('');

  const filtered = roles.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return r.name.toLowerCase().includes(q) || r.typeLabel.toLowerCase().includes(q);
  });

  const handleConfirm = () => {
    const chosen = roles.find((r) => r.idAccessRole === sel);
    if (!chosen) return;
    profile.selectRole(chosen);
    void navigate('/');
  };

  const handleLogout = () => {
    profile.clearProfile();
    auth.logout();
    void navigate('/login');
  };

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        background: T.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient gradient circles */}
      <div
        style={{
          position: 'absolute',
          width: 560,
          height: 560,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${C.blue}12 0%, transparent 70%)`,
          top: -200,
          left: -200,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: 420,
          height: 420,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${C.success}0e 0%, transparent 70%)`,
          bottom: -130,
          right: -110,
          pointerEvents: 'none',
        }}
      />

      {/* Centered card */}
      <div
        data-testid="profile-card"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 452,
          height: '88vh',
          maxHeight: 720,
          borderRadius: 22,
          border: `1px solid ${T.border}`,
          background: T.surfaceBg,
          boxShadow: '0 24px 60px rgba(16,24,40,.16), 0 6px 18px rgba(16,24,40,.08)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          margin: '0 16px',
        }}
      >
        {/* Logo — centered at top inside the card */}
        <div
          style={{
            textAlign: 'center',
            paddingTop: 30,
            paddingBottom: 0,
            flexShrink: 0,
          }}
        >
          <img
            src="/logo-myintelli-clock-full.png"
            alt="MyIntelli Clock"
            style={{ height: 44, width: 'auto', display: 'inline-block' }}
          />
        </div>

        {/* Logout button — absolute top-right inside the card */}
        <button
          onClick={handleLogout}
          aria-label={t('profile.signOut')}
          title={t('profile.signOut')}
          style={{
            position: 'absolute',
            top: 28,
            right: 30,
            background: 'transparent',
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            width: 36,
            height: 36,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: T.text,
            flexShrink: 0,
          }}
        >
          <svg
            width={15}
            height={15}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </button>

        {/* Page header: title + description (no hexagon/cube icon) */}
        <div style={{ flexShrink: 0, padding: '24px 28px 16px' }}>
          <div style={{ textAlign: 'center', marginBottom: 18 }}>
            <h1
              style={{
                fontSize: 24,
                fontWeight: 800,
                color: T.clockText,
                margin: '0 0 6px',
                letterSpacing: '-.02em',
              }}
            >
              {t('profile.title')}
            </h1>
            <div
              style={{
                fontSize: 13,
                color: T.textMuted,
                maxWidth: 380,
                margin: '0 auto',
              }}
            >
              {t('profile.description')}
            </div>
          </div>

          {/* Search */}
          <div style={{ position: 'relative' }}>
            <svg
              width={16}
              height={16}
              viewBox="0 0 24 24"
              fill="none"
              stroke={T.textMuted}
              strokeWidth="2"
              strokeLinecap="round"
              style={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
              }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              placeholder={t('profile.searchPlaceholder')}
              style={{
                width: '100%',
                padding: '14px 16px 14px 44px',
                borderRadius: 12,
                border: `1.5px solid ${T.border}`,
                background: T.bg,
                color: T.text,
                fontFamily: 'inherit',
                fontSize: 13,
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color .15s, box-shadow .15s',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = C.blue;
                e.currentTarget.style.boxShadow = `0 0 0 4px ${C.blue}15`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = T.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Scrollable role list */}
        <div
          style={{
            flex: '1 1 0',
            overflowY: 'auto',
            padding: '4px 28px 16px',
            minHeight: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              paddingTop: 8,
            }}
          >
            {isLoading && <LoadingState T={T} />}
            {isError && (
              <ErrorState
                T={T}
                C={C}
                onRetry={() => {
                  window.location.reload();
                }}
              />
            )}
            {!isLoading && !isError && filtered.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '60px 0',
                  color: T.textMuted,
                  fontSize: 13,
                }}
              >
                {search
                  ? t('profile.noResultsFor', { search })
                  : t('profile.noProfiles')}
              </div>
            )}
            {!isLoading &&
              !isError &&
              filtered.map((role) => (
                <RoleCard
                  key={role.idAccessRole}
                  role={role}
                  active={sel === role.idAccessRole}
                  onSelect={() => {
                    setSel(role.idAccessRole);
                  }}
                  C={C}
                  T={T}
                />
              ))}
          </div>
        </div>

        {/* Footer CTA */}
        <div
          style={{
            flexShrink: 0,
            padding: '14px 28px 20px',
            borderTop: `1px solid ${T.border}`,
            background: T.surfaceBg,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={handleConfirm}
              disabled={!sel}
              style={{
                padding: '14px 40px',
                borderRadius: 12,
                border: 'none',
                background: sel ? C.success : T.border,
                color: '#fff',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 700,
                cursor: sel ? 'pointer' : 'not-allowed',
                boxShadow: sel ? `0 8px 20px ${C.success}40` : 'none',
                transition: 'transform .1s, background .15s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
              onMouseDown={(e) => {
                if (sel) e.currentTarget.style.transform = 'scale(.98)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {t('profile.loadProfile')}
              <svg
                width={14}
                height={14}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────

interface ThemeProps {
  T: ReturnType<typeof useTheme>['T'];
  C: ReturnType<typeof useTheme>['C'];
}

function RoleCard({
  role,
  active,
  onSelect,
  C,
  T,
}: { role: AccessRole; active: boolean; onSelect: () => void } & ThemeProps) {
  const { t } = useTranslation('common');
  return (
    <button
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '16px',
        borderRadius: 14,
        border: `2px solid ${active ? C.blue : T.border}`,
        background: active ? `${C.blue}08` : T.bg,
        fontFamily: 'inherit',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all .15s',
        boxShadow: active
          ? `0 6px 18px ${C.blue}25`
          : '0 1px 2px rgba(0,0,0,.02)',
        width: '100%',
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = `${C.blue}66`;
          e.currentTarget.style.transform = 'translateY(-1px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = T.border;
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
    >
      {/* icon */}
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: 11,
          background: active ? C.blue : `${C.blue}15`,
          color: active ? '#fff' : C.blue,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all .15s',
        }}
      >
        <svg
          width={20}
          height={20}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
      </div>

      {/* text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: T.text,
            letterSpacing: '-.01em',
          }}
        >
          {role.name}
        </div>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>
          {role.typeLabel}
        </div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: active ? C.blue : T.textMuted,
            marginTop: 6,
            letterSpacing: '.04em',
          }}
        >
          {role.employeeCount.toLocaleString('es')} {t('profile.employees')}
        </div>
      </div>

      {/* radio */}
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          border: `2px solid ${active ? C.blue : T.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'border-color .15s',
        }}
      >
        {active && (
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: C.blue,
            }}
          />
        )}
      </div>
    </button>
  );
}

function LoadingState({ T }: { T: ThemeProps['T'] }) {
  const { t } = useTranslation('common');
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '60px 0',
        color: T.textMuted,
        fontSize: 13,
      }}
    >
      <svg
        width={24}
        height={24}
        viewBox="0 0 24 24"
        className="mi-anim-spin"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        style={{ display: 'block', margin: '0 auto 12px' }}
      >
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
      {t('profile.loadingProfiles')}
    </div>
  );
}

function ErrorState({
  T,
  C,
  onRetry,
}: {
  T: ThemeProps['T'];
  C: ThemeProps['C'];
  onRetry: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{ fontSize: 13, color: C.danger, marginBottom: 16 }}>
        {t('profile.errorLoad')}
      </div>
      <button
        onClick={onRetry}
        style={{
          padding: '8px 20px',
          borderRadius: 8,
          border: `1.5px solid ${T.border}`,
          background: 'transparent',
          color: T.text,
          fontFamily: 'inherit',
          fontSize: 12,
          cursor: 'pointer',
        }}
      >
        {t('profile.retry')}
      </button>
    </div>
  );
}
